import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../redux/slices/appContext'
import { postNamedCommand, postNamedQuery, wealthboxGetUnlinkedTags } from '../service'
import { QUERY_KEYS } from './queryKeys'

export const useWealthboxUnlinkedTags = (options = {}) => {
  const { userId } = useAppContext()
  const { mapper = null } = options
  return useQuery({
    queryKey: [QUERY_KEYS.wealthboxUnlinkedTags, userId],
    queryFn: async () => {
      const { data } = await wealthboxGetUnlinkedTags()
      return data
    },
    select: mapper
  })
}

// Generic Wealthbox domain query hook. Mirrors useRedtailDomainQuery so smart
// components can swap between Redtail and Wealthbox handlers without rewriting
// the cache-key shape. The backend handler (execute-wealthbox-domain-query)
// resolves a per-user OAuth bearer token via getOrCreateWealthboxApiToken.
// We gate on `!!clientId` like the Redtail equivalent so the query doesn't
// fire with `clientId: undefined` during app context init - callers that
// need firm-wide queries (no client binding) should use the preset hooks
// (useWealthboxMetricPreset / useWealthboxListPreset), which talk to
// postNamedQuery directly without per-client gating.
export const useWealthboxDomainQuery = (queryName, request, options = {}) => {
  const { userId, clientId } = useAppContext()
  const { enabled = true, ...restOptions } = options
  return useQuery({
    queryKey: [QUERY_KEYS.wealthboxDomainQuery, userId, clientId, queryName, request],
    queryFn: async () => {
      const { data } = await postNamedQuery('coreData', 'execute-wealthbox-domain-query', {
        query: queryName,
        clientId,
        request
      })
      return data
    },
    enabled: enabled && !!clientId,
    ...restOptions
  })
}

const BATCH_SIZE = 10

// Mirrors postBatchRedtailQueries: chunks any number of queries into groups
// of 10 (the backend handler's max) and merges the results back into a single
// stream so consumers don't need to think about the limit.
export const postBatchWealthboxQueries = async (queries, { clientId } = {}) => {
  const chunks = []
  for (let i = 0; i < queries.length; i += BATCH_SIZE) {
    chunks.push(queries.slice(i, i + BATCH_SIZE))
  }

  const allResults = []
  for (const chunk of chunks) {
    const { data } = await postNamedQuery('coreData', 'execute-wealthbox-domain-queries', {
      queries: chunk,
      clientId
    })
    allResults.push(...(data?.results ?? []))
  }

  return { results: allResults }
}

// Resolves a Summit clientId to its Wealthbox external contact id (the numeric
// portion of the `account:<id>` value stored on levels.integrationsLevels).
// Returns `{ linked, externalId }` so callers can render a NotLinked state when
// no mapping exists (mirrors useRedtailClientLink's contract).
// `options.enabled` gates whether the resolve query fires - callers that don't
// need the link (e.g. firm-wide or My Book scoped metrics) should pass
// `{ enabled: false }` so we don't pay for the identifier resolve when its
// result wouldn't be consumed.
export const useWealthboxClientLink = (clientId, { enabled = true } = {}) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.wealthboxClientLink, userId, clientId],
    queryFn: async () => {
      try {
        const { data } = await postNamedQuery('coreData', 'resolve-wealthbox-identifier', {
          type: 'client',
          id: clientId
        })
        return { linked: true, externalId: data.externalId }
      } catch (err) {
        if (err?.response?.status === 400) {
          return { linked: false, externalId: null }
        }
        throw err
      }
    },
    enabled: enabled && !!clientId
  })
}

// Generic Wealthbox domain command hook. Mirrors useWealthboxDomainQuery but
// for mutating commands (`createTask`, `modifyTask`, `completeTask`,
// `deleteTask`, etc.). Caller passes `{ command, request }` to mutate(); we
// route via `execute-wealthbox-domain-command` so the server-side dispatcher
// (resolveWealthboxOperation) handles token resolution, scope translation,
// 401 → silent refresh, and command-audit logging.
//
// Provide `invalidateQueryKeys: [[QUERY_KEYS.wealthboxDomainQuery, ...]]` if
// you want successful mutations to bust specific React Query caches (e.g.
// the task-list query so a freshly-created task shows up). Each entry is
// passed verbatim to queryClient.invalidateQueries({ queryKey }).
export const useWealthboxDomainCommand = ({ invalidateQueryKeys = [] } = {}) => {
  const queryClient = useQueryClient()
  const { clientId } = useAppContext()
  return useMutation({
    mutationFn: async ({ command, request }) => {
      const { data } = await postNamedCommand('coreData', 'execute-wealthbox-domain-command', {
        command,
        clientId,
        request
      })
      return data
    },
    onSuccess: () => {
      for (const key of invalidateQueryKeys) {
        queryClient.invalidateQueries({ queryKey: key }).catch(console.error)
      }
    }
  })
}
